export const MDM_POLICIES = {
  TITLE: 'Device policies',
  SUBTITLE: 'Configure device policies for your organization',
  NEW_POLICY: 'New policy',
  TABS: {
    GENERAL: 'General',
    CIS: 'CIS Benchmark',
    LIBRARY: 'Qalea library',
    ORGANIZATION: 'Organization',
    HISTORY: 'Deployment history',
  },
  SECTIONS: {
    OS_UPDATES: 'OS Updates',
    DISK_ENCRYPTION: 'Disk Encryption',
  },
  DISK_ENCRYPTION: {
    ENABLE: 'Enable Disk Encryption',
    HOST_STATUS: 'Host Status',
    MACOS: 'macOS',
    WINDOWS: 'Windows',
    LINUX: 'Linux',
    TOTAL: 'Total',
    LOADING: 'Syncing with hosts (may take a few minutes)...',
    STATUS: {
      VERIFIED: 'Verified',
      VERIFYING: 'Verifying',
      ACTION_REQUIRED: 'Action Required',
      ENFORCING: 'Enforcing',
      FAILED: 'Failed',
      REMOVING_ENFORCEMENT: 'Removing',
    },
  },
  OS_UPDATES: {
    TITLE: 'OS Updates',
    LAPTOPS: 'Laptops',
    PHONES: 'Phones',
    CONFIRM: {
      TITLE: (platform: string) => `Enforce ${platform} update?`,
      ACTION: 'Enforce update',
      NOTE: "Please take into account users can't skip it or postpone it.",
      UPDATE: 'Update',
      DEADLINE: 'Deadline',
      FORCED_RESTART: 'Forced restart',
      NEWEST_VERSION: 'Newest supported version',
      AVAILABLE_UPDATES: 'Available updates',
      DAYS_AFTER_RELEASE: (days: number) => `${days} ${days === 1 ? 'day' : 'days'} after release`,
      AT_DEADLINE: 'At deadline',
      DAYS_AFTER_DEADLINE: (days: number) => `${days} ${days === 1 ? 'day' : 'days'} after deadline`,
    },
    MACOS: {
      TITLE: 'macOS Updates',
      TARGET: 'Target',
      TARGET_NONE: 'No updates enforced',
      TARGET_SPECIFIC: 'Specific version',
      TARGET_LATEST: 'Latest version',
      MINIMUM_VERSION: 'Minimum version',
      DEADLINE: 'Deadline',
      DAYS_AFTER_RELEASE: 'days after release',
      UPDATE_NEW_HOSTS: 'Auto-update new hosts',
      REQUIRED: 'Required',
    },
    IOS: {
      TITLE: 'iOS Updates',
      TARGET: 'Target',
      TARGET_NONE: 'No updates enforced',
      TARGET_SPECIFIC: 'Specific version',
      TARGET_LATEST: 'Latest version',
      MINIMUM_VERSION: 'Minimum version',
      DEADLINE: 'Deadline',
      DAYS_AFTER_RELEASE: 'days after release',
      REQUIRED: 'Required',
    },
    ANDROID: {
      TITLE: 'Android Updates',
      UPDATE_POLICY: 'Update policy',
      TYPE_AUTOMATIC: 'Automatic',
      TYPE_WINDOWED: 'Daily window',
      TYPE_POSTPONE: 'Postpone',
      INSTALL_WINDOW: 'Install window',
      WINDOW_SEPARATOR: 'to',
    },
    WINDOWS: {
      TITLE: 'Windows Updates',
      TARGET: 'Target',
      TARGET_NONE: 'No updates enforced',
      TARGET_LATEST: 'Latest updates',
      DEADLINE_PREFIX: 'An update should be installed before',
      GRACE_PERIOD_PREFIX: 'Otherwise a restart to install will happen after',
      DAYS: 'days',
      REQUIRED: 'Required',
    },
  },
}

export const MDM_POLICIES_MESSAGES = {
  UPDATED: 'Policy updated successfully',
  FAILED_TO_UPDATE: 'Failed to update policy',
  UNSUPPORTED_VERSION:
    'Apple doesn\'t offer this version. Enter a version Apple currently offers, or choose "Latest version".',
}

export const MDM_PROFILES = {
  SUMMARY: {
    HOSTS_TARGETED: 'hosts targeted',
    VERIFIED: 'verified',
    FAILED: 'failed',
  },
  TABLE: {
    COLUMNS: {
      NAME: 'Name',
      PLATFORM: 'Platform',
      STATUS: 'Status',
      DATE: 'Date',
      HOSTS: 'Hosts',
    },
    NO_HOSTS: 'No hosts',
    HOSTS: 'hosts',
  },
  STATUS: {
    DEPLOYED: 'Deployed',
    PENDING: 'Pending',
    FAILED: 'Failed',
    VERIFIED: 'Deployed',
    VERIFYING: 'Verifying',
    REMOVED: 'Removed',
  },
  ACTIONS: {
    DEPLOY: 'Deploy',
    DEPLOY_PROFILE: 'Deploy Policy',
    CREATE_PROFILE: 'Create Policy',
    CANCEL: 'Cancel',
    DONE: 'Done',
    DEPLOYING: 'Deploying...',
  },
  DRAWER: {
    TITLE: 'Custom Policy Details',
    DEPLOYMENT_DETAILS: 'Deployment Details',
    REFRESH: 'Refresh',
    PENDING_HINT: 'Hosts apply it at their next check-in, which can take a while',
    SECTIONS: {
      DESCRIPTION: 'Description',
      PLATFORM: 'Platform',
      PROFILE_CONTENT: 'Policy Content',
      DEPLOYMENT_STATUS: 'Deployment Status',
      HOST_BREAKDOWN: 'Host Breakdown',
    },
    EMPTY: {
      DESCRIPTION: 'No description',
      CONTENT: 'No policy content',
    },
    HOST_BREAKDOWN: {
      HOST: 'Host',
      STATUS: 'Status',
      DETAIL: 'Detail',
      NO_HOSTS: 'No hosts deployed',
    },
  },
  FORM: {
    TITLE: 'New Policy',
    FIELDS: {
      NAME: 'Name',
      NAME_PLACEHOLDER: 'Enter policy name',
      DESCRIPTION: 'Description',
      DESCRIPTION_PLACEHOLDER: 'Enter policy description',
      PLATFORM: 'Platform',
      CONTENT: 'Policy Content',
      CONTENT_PLACEHOLDER: 'Enter policy XML/JSON content',
    },
  },
  DEPLOY_DIALOG: {
    DESCRIPTION: 'Select groups to deploy this policy.',
    SUCCESS_DESCRIPTION: 'Policy deployed successfully to all selected groups.',
    SEARCH_PLACEHOLDER: 'Search groups...',
  },
  PLATFORMS: {
    MACOS: 'macOS',
    WINDOWS: 'Windows',
    IOS: 'iOS',
    ANDROID: 'Android',
  },
  MESSAGES: {
    PROFILE_CREATED: 'Profile created successfully',
    PROFILE_CREATE_FAILED: 'Failed to create profile',
    PROFILE_DELETED: 'Profile deleted successfully',
    PROFILE_DELETE_FAILED: 'Failed to delete profile',
    NAME_CONFLICT: (name: string) => `A policy named "${name}" already exists, please choose another name.`,
    CONTENT_CONFLICT: 'A policy with the same content already exists, please modify it before saving.',
  },
  DELETE_DIALOG: {
    TITLE: 'Delete Profile',
    DESCRIPTION_PREFIX: 'Are you sure you want to delete',
    DESCRIPTION_SUFFIX: '?',
    HOSTS_PREFIX: 'The policy will be',
    HOSTS_EMPHASIS: 'removed',
    HOSTS_SUFFIX: 'from all its hosts.',
    IRREVERSIBLE: 'This action cannot be undone.',
    CANCEL: 'Cancel',
    CONFIRM: 'Delete',
  },
}
